/**
 * Client-side encryption key utilities + AES-GCM encrypt/decrypt.
 * Keys are generated via Web Crypto API (AES-GCM 256-bit) and stored
 * exclusively in localStorage — the server never sees them.
 *
 * Encrypted values are a single base64 string: first 12 bytes = IV,
 * remainder = ciphertext. No separate nonce field needed.
 */

const STORAGE_PREFIX = "enckey_";

/** Generate a cryptographically secure AES-GCM 256-bit key, returned as base64. */
export async function generateEncryptionKey(): Promise<string> {
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const raw = await crypto.subtle.exportKey("raw", key);
  return btoa(String.fromCharCode(...new Uint8Array(raw)));
}

/** Validate that a string is a valid base64-encoded 256-bit key (44 chars). */
export function isValidEncryptionKey(key: string): boolean {
  try {
    const decoded = atob(key);
    return decoded.length === 32; // 256 bits = 32 bytes
  } catch {
    return false;
  }
}

/** Get the stored encryption key for a client (from localStorage). */
export function getClientKey(clientId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${clientId}`);
  } catch {
    return null;
  }
}

/** Store an encryption key for a client in localStorage. */
export function setClientKey(clientId: string, key: string): void {
  localStorage.setItem(`${STORAGE_PREFIX}${clientId}`, key);
}

/** Remove the stored encryption key for a client. */
export function removeClientKey(clientId: string): void {
  localStorage.removeItem(`${STORAGE_PREFIX}${clientId}`);
}

// ---------------------------------------------------------------------------
// AES-GCM encrypt / decrypt
// ---------------------------------------------------------------------------

/** Import a base64-encoded raw key into a CryptoKey. */
async function importKey(keyBase64: string, usage: KeyUsage[]): Promise<CryptoKey> {
  const raw = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, usage);
}

/**
 * Encrypt a plaintext string with AES-GCM 256.
 * Returns a single base64 string (12-byte IV prepended to ciphertext).
 * Returns the original value unchanged if it's empty.
 */
export async function encrypt(plaintext: string, keyBase64: string): Promise<string> {
  if (!plaintext) return plaintext;
  const key = await importKey(keyBase64, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ct), iv.length);
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt a value produced by `encrypt()`.
 * Returns `"[Encrypted]"` on any failure (wrong key, corrupt data, etc.).
 */
export async function decrypt(encrypted: string, keyBase64: string): Promise<string> {
  if (!encrypted) return encrypted;
  try {
    const key = await importKey(keyBase64, ["decrypt"]);
    const combined = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ct = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    return new TextDecoder().decode(decrypted);
  } catch {
    return "[Encrypted]";
  }
}

/**
 * Encrypt specific fields of an object in-place (returns a shallow copy).
 */
export async function encryptFields<T extends Record<string, unknown>>(
  obj: T,
  fields: (keyof T)[],
  keyBase64: string,
): Promise<T> {
  const result = { ...obj };
  for (const field of fields) {
    const val = result[field];
    if (typeof val === "string") {
      (result as Record<string, unknown>)[field as string] = await encrypt(val, keyBase64);
    }
  }
  return result;
}

/**
 * Encrypt a raw binary Uint8Array with AES-GCM 256.
 * Returns a single base64 string (12-byte IV prepended to ciphertext).
 */
export async function encryptBinary(data: Uint8Array, keyBase64: string): Promise<string> {
  const key = await importKey(keyBase64, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data as ArrayBufferView<ArrayBuffer>);
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ct), iv.length);
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt a value produced by `encryptBinary()`.
 * Returns the raw bytes, or null on any failure.
 */
export async function decryptBinary(encrypted: string, keyBase64: string): Promise<Uint8Array | null> {
  if (!encrypted) return null;
  try {
    const key = await importKey(keyBase64, ["decrypt"]);
    const combined = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ct = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    return new Uint8Array(decrypted);
  } catch {
    return null;
  }
}

/**
 * Decrypt specific fields of an object (returns a shallow copy).
 */
export async function decryptFields<T extends Record<string, unknown>>(
  obj: T,
  fields: (keyof T)[],
  keyBase64: string,
): Promise<T> {
  const result = { ...obj };
  for (const field of fields) {
    const val = result[field];
    if (typeof val === "string" && val) {
      (result as Record<string, unknown>)[field as string] = await decrypt(val, keyBase64);
    }
  }
  return result;
}
